import { useEffect, useRef, useState } from 'react'
import { AppModal, PaginationBar } from '@/components/ui'
import { Alert, Anchor, Button, Group, NativeSelect, Stack, Table, Text } from '@mantine/core'
import { periodParamsToAnalyticsQuery } from '@/lib/analytics-query'
import { api } from '@/lib/api'
import { useOpenTicketFromList } from '@/lib/open-ticket-navigation'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { DashboardPeriodParams } from '@/lib/dashboard-period'
import type { OperatorTicketListRow, ProjectRow, TicketListItem } from '@/types/api'
import {
  PRODUCT_TICKETS_PAGE_SIZE_OPTIONS,
  formatNumber,

  type AiReasonKey,
} from '@/pages/analytics/analytics-new-utils'

export function ProductTicketsModal({ opened, onClose, project, periodParams, withoutAI = false }: { opened: boolean; onClose: () => void; project: ProjectRow | null; periodParams: DashboardPeriodParams; withoutAI?: boolean }) {
  const openTicket = useOpenTicketFromList()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<TicketListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<(typeof PRODUCT_TICKETS_PAGE_SIZE_OPTIONS)[number]>(20)
  const [frozenPeriodParams, setFrozenPeriodParams] = useState(periodParams)
  const [frozenWithoutAI, setFrozenWithoutAI] = useState(withoutAI)
  const modalScopeRef = useRef('')
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  useEffect(() => {
    if (!opened) {
      modalScopeRef.current = ''
      return
    }
    const scope = project?.id || ''
    if (modalScopeRef.current === scope) return
    modalScopeRef.current = scope
    setFrozenPeriodParams(periodParams)
    setFrozenWithoutAI(withoutAI)
    setPage(1)
  }, [opened, project?.id, periodParams, withoutAI])

  useEffect(() => {
    if (!opened) return
    setPage(1)
  }, [opened, pageSize])

  useEffect(() => {
    if (!opened || !project?.id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api.analyticsProjectTickets({ ...periodParamsToAnalyticsQuery(frozenPeriodParams), project_id: project.id, page, page_size: pageSize, without_ai_assistant: frozenWithoutAI })
      .then((res) => {
        if (cancelled) return
        setRows(res.tickets ?? [])
        setTotal(res.total ?? 0)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Не удалось загрузить обращения')
        setRows([])
        setTotal(0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [opened, project?.id, frozenPeriodParams, page, pageSize, frozenWithoutAI])

  return (
    <AppModal opened={opened} onClose={onClose} title={`Обращения проекта: ${project?.name || '—'}`} size="xl">
      <Stack gap="sm">
        {error ? <Alert color="red">{error}</Alert> : null}
        <Group justify="space-between" align="center" gap="sm" wrap="wrap">
          <Group gap="xs" wrap="wrap" align="center">
            <Text size="xs" c="dimmed">Показывать:</Text>
            <NativeSelect
              size="xs"
              data={PRODUCT_TICKETS_PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
              value={String(pageSize)}
              onChange={(event) => {
                const next = Number(event.currentTarget.value)
                if (!PRODUCT_TICKETS_PAGE_SIZE_OPTIONS.includes(next as (typeof PRODUCT_TICKETS_PAGE_SIZE_OPTIONS)[number])) return
                setPageSize(next as (typeof PRODUCT_TICKETS_PAGE_SIZE_OPTIONS)[number])
              }}
              w={72}
            />
            <Text size="xs" c="dimmed">записей</Text>
          </Group>
          <Text size="xs" c="dimmed">Всего: {formatNumber(total)}. Страница {page} из {pageCount}.</Text>
          <PaginationBar page={page} pageCount={pageCount} total={total} disabled={loading} onPageChange={setPage} />
        </Group>
        <Table striped highlightOnHover layout="fixed">
          <Table.Thead>
            <Table.Tr><Table.Th>Обращение</Table.Th><Table.Th>Тема</Table.Th><Table.Th>Статус</Table.Th><Table.Th>Создано</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">Загрузка…</Text></Table.Td></Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">Нет записей</Text></Table.Td></Table.Tr>
            ) : rows.map((ticket) => (
              <Table.Tr key={ticket.id}>
                <Table.Td>
                  <Anchor component="button" size="sm" onClick={() => openTicket(ticket)}>
                    {ticket.project_slug ? `${ticket.project_slug}-${ticket.ticket_number}` : `#${ticket.ticket_number}`}
                  </Anchor>
                </Table.Td>
                <Table.Td><Text size="sm" truncate>{ticket.subject || '—'}</Text></Table.Td>
                <Table.Td><Text size="sm">{ticket.status || '—'}</Text></Table.Td>
                <Table.Td><Text size="sm">{ticket.created_at ? formatDateTimeDot(ticket.created_at) : '—'}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </AppModal>
  )
}

export function AiTransferTicketsModal({ opened, onClose, reason, label, analyticsParams }: { opened: boolean; onClose: () => void; reason: AiReasonKey; label: string; analyticsParams: Record<string, string | number | boolean> }) {
  const openTicket = useOpenTicketFromList()
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<OperatorTicketListRow[]>([])
  const [total, setTotal] = useState(0)
  const [frozenAnalyticsParams, setFrozenAnalyticsParams] = useState<Record<string, string | number | boolean>>({})
  const modalScopeRef = useRef('')
  const pageSize = 20

  useEffect(() => {
    if (!opened) {
      modalScopeRef.current = ''
      return
    }
    const scope = `${reason}`
    if (modalScopeRef.current === scope) return
    modalScopeRef.current = scope
    setFrozenAnalyticsParams(analyticsParams)
    setPage(1)
  }, [opened, reason, analyticsParams])

  useEffect(() => {
    if (!opened) return
    let cancelled = false
    setLoading(true)
    api.analyticsAiTransferTickets({
      reason,
      page,
      page_size: pageSize,
      ...frozenAnalyticsParams,
    })
      .then((res) => {
        if (cancelled) return
        setRows(res.tickets ?? [])
        setTotal(res.total ?? 0)
      })
      .catch(() => {
        if (cancelled) return
        setRows([])
        setTotal(0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [opened, reason, page, frozenAnalyticsParams])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <AppModal opened={opened} onClose={onClose} title={`Передачи: ${label}`} size="xl">
      <Stack gap="md">
        <Table striped highlightOnHover layout="fixed">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Обращение</Table.Th>
              <Table.Th>Проект</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Создан</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={4}><Text size="sm" c="dimmed">Загрузка…</Text></Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}><Text size="sm" c="dimmed">Нет записей</Text></Table.Td>
              </Table.Tr>
            ) : rows.map((ticket) => (
              <Table.Tr key={ticket.id}>
                <Table.Td>
                  <Anchor component="button" size="sm" onClick={() => openTicket({
                    id: ticket.id,
                    ticket_number: ticket.ticket_number,
                    project_slug: ticket.project_slug ?? null,
                    subject: ticket.subject ?? '',
                    status: ticket.status ?? '',
                    priority: 'normal',
                    customer_name: null,
                    customer_email: null,
                    customer_avatar_url: null,
                    created_at: ticket.created_at ?? '',
                    updated_at: ticket.created_at ?? '',
                    project_id: ticket.project_id ?? '',
                    project_name: ticket.project_name,
                    channel_name: null,
                    channel_type: null,
                    last_update_at: null,
                    last_update_by: null,
                    last_update_sender_name: null,
                    messages_count: 0,
                  } as TicketListItem)}>
                    {ticket.project_slug ? `${ticket.project_slug}-${ticket.ticket_number}` : `#${ticket.ticket_number}`}
                  </Anchor>
                </Table.Td>
                <Table.Td><Text size="sm" truncate>{ticket.project_name || '—'}</Text></Table.Td>
                <Table.Td><Text size="sm">{ticket.status || '—'}</Text></Table.Td>
                <Table.Td><Text size="sm">{ticket.created_at ? formatDateTimeDot(ticket.created_at) : '—'}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {totalPages > 1 ? (
          <Group justify="space-between">
            <Text size="xs" c="dimmed">Всего: {total}. Страница {page} из {totalPages}.</Text>
            <Group gap="xs">
              <Button size="xs" variant="default" disabled={page <= 1} onClick={() => setPage(1)}>Первая</Button>
              <Button size="xs" variant="default" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>←</Button>
              <Button size="xs" variant="default" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>→</Button>
            </Group>
          </Group>
        ) : null}
      </Stack>
    </AppModal>
  )
}
